import { idPrefixes } from '@/enums/signup'
import { defaultState as womenDefaultState } from '@/enums/womenSignup'
import { Orientations } from '@/enums/products'
import { MembershipTypes } from '@/enums/member'
import type { MembershipTypeReference } from '@/types/member'

interface ProspectMetadataTracking {
  utmSource?: string
  utmMedium?: string
  utmCampaign?: string
  landingPageUrl?: string
  otaAttribution?: string
}

interface ProspectMetadataEverflow {
  transactionId: string
  subIds?: string
  sourceId?: string
  affId?: string
  efAdditionalParams?: string
  mcanismTrackId?: string
  m4mTid?: string
  adtrId?: string
}

interface ProspectMetadataSnapchat {
  clickId: string
  browserId?: string
  campaign?: string
  medium?: string
}

interface ProspectMetadataFacebook {
  clickId: string
  browserId?: string
}

interface ProspectMetadataTiktok {
  clickId: string
  browserId?: string
}

interface ProspectMetadataGoogle {
  clickId: string
  gbraid?: string
  wbraid?: string
}

interface ProspectMetadataInternal {
  convertAb?: string
  convertContextId?: string
  cookieConsent?: string
}

interface ProspectMetadata {
  everflow?: ProspectMetadataEverflow
  snapchat?: ProspectMetadataSnapchat
  facebook?: ProspectMetadataFacebook
  tiktok?: ProspectMetadataTiktok
  google?: ProspectMetadataGoogle
  tracking?: ProspectMetadataTracking
  internal?: ProspectMetadataInternal
  tags?: string[]
  variants?: unknown
}

export interface ProspectInputProps {
  firstName?: string
  lastName?: string
  email?: string
  password?: string
  locale?: string
  referralCode?: string | null
  productGroupSizeDesigns?: unknown
  country?: string
  membershipType?: string
  promotionalEmailsEnabled?: boolean
  attributionIds?: Omit<ProspectMetadata, 'tags' | 'variants'>
  tags?: string[]
  userVariants?: unknown
}

export interface ProspectAddressInput {
  building?: string
  floor?: string
  street?: string
  streetType?: string
  country?: string
  houseNumber?: string | number | null
  annex?: string
  houseNumberAddition?: string
  postalCode?: string
  city?: string
  region?: string
  loqateAddressId?: string | null
  [key: string]: unknown
}

interface WomenSignupFormSlot {
  size: { reference: string | null; label?: string | null }
  design: { name?: string | null; slug: string | null }
}

export interface WomenSignupForm {
  [Orientations.womenbralette]: WomenSignupFormSlot
  [Orientations.womenhipster]: WomenSignupFormSlot
  [key: string]: unknown
}

type ProductGroupSizeDesign = {
  productGroupSize: string
  freeProductGroupSize: string
  freeProductDesign: string
}

/**
 * Complements the ID values where needed
 */
export const mapToProspectProps = (props: ProspectInputProps = {}) => ({
  firstName: props.firstName,
  lastName: props.lastName,
  email: props.email,
  password: props.password,
  locale: props.locale,
  referralCode: props.referralCode,
  productGroupSizeDesigns: props.productGroupSizeDesigns,
  country: `${idPrefixes.country}${props.country}`,
  membershipType: `${idPrefixes.membershipType}${props.membershipType}`,
  promotionalEmailsEnabled: props.promotionalEmailsEnabled,
  metadata: {
    ...props.attributionIds,
    tags: props.tags,
    ...(props.userVariants ? { variants: props.userVariants } : {})
  } satisfies ProspectMetadata
})

export const mapAddressToProspectProps = (address: ProspectAddressInput = {}) => ({
  ...address,
  houseNumberAddition: address.annex,
  country: `${idPrefixes.country}${address.country}`,
  ...(address.loqateAddressId ? { loqateAddressId: address.loqateAddressId } : {})
})

/**
 * Removes the address data of the object
 */
export const stripAddressProps = <T extends ProspectAddressInput>(
  props: T = {} as T
): Omit<T, 'building' | 'floor' | 'street' | 'country' | 'houseNumber' | 'annex' | 'postalCode' | 'city' | 'region'> => {
  const {
    building: _building,
    floor: _floor,
    street: _street,
    country: _country,
    houseNumber: _houseNumber,
    annex: _annex,
    postalCode: _postalCode,
    city: _city,
    region: _region,
    ...prospectWithoutAddress
  } = props

  return prospectWithoutAddress
}

export const getWomenProductGroupSizeDesigns = (
  signupForm: WomenSignupForm = womenDefaultState.form as unknown as WomenSignupForm
): ProductGroupSizeDesign[] => {
  const top: ProductGroupSizeDesign = {
    productGroupSize: `${idPrefixes.productGroupSizes}${signupForm[Orientations.womenbralette].size.reference}`,
    freeProductGroupSize: `${idPrefixes.productGroupSizes}${signupForm[Orientations.womenbralette].size.reference}`,
    freeProductDesign: `${idPrefixes.designReference}${signupForm[Orientations.womenbralette].design.slug}`
  }

  const hipster: ProductGroupSizeDesign = {
    productGroupSize: `${idPrefixes.productGroupSizes}${signupForm[Orientations.womenhipster].size.reference}`,
    freeProductGroupSize: `${idPrefixes.productGroupSizes}${signupForm[Orientations.womenhipster].size.reference}`,
    freeProductDesign: `${idPrefixes.designReference}${signupForm[Orientations.womenhipster].design.slug}`
  }

  return [top, hipster]
}

export const getMembershipTypeUrl = (
  membershipTypeReference: MembershipTypeReference | string = MembershipTypes.menOriginals
): 'men-one' | 'boys-originals' | 'men-originals' => {
  if (membershipTypeReference === MembershipTypes.menOne) {
    return 'men-one'
  }

  if (membershipTypeReference === MembershipTypes.boysOriginals) {
    return 'boys-originals'
  }

  return 'men-originals'
}
